import { FilterOptions } from "@tfg/ui"
import { useCallback } from "react"

import {
  FindContactRequestFiltersDto,
  findSupplierMappingFilteringOptions,
  FindSupplierMappingFilteringOptionsQueryResponse,
  useFindSupplierMappingFilteringOptions,
} from "~/gen/api"

export type ResolverProps<
  K extends keyof FindSupplierMappingFilteringOptionsQueryResponse,
> = {
  // The label of the filter
  fieldLabel: string
  // The key of the filter in the request body
  fieldKey: keyof FindContactRequestFiltersDto
  // The key of the filter in the FindSupplierMappingFilteringOptionsQueryResponse
  key: K
  // The function to resolve the label of the item
  resolveLabel: (
    item: FindSupplierMappingFilteringOptionsQueryResponse[K][number]
  ) => string
  // The function to resolve the value of the item
  resolveValue: (
    item: FindSupplierMappingFilteringOptionsQueryResponse[K][number]
  ) => string
}

export const useBusinessPartnerMultiSelectFilterOptionResolver = () => {
  const { data: filteringOptionsData, isFetching: isFilteringOptionsLoading } =
    useFindSupplierMappingFilteringOptions()

  return useCallback(
    function <
      K extends keyof FindSupplierMappingFilteringOptionsQueryResponse,
    >({
      fieldLabel,
      fieldKey,
      key,
      resolveLabel,
      resolveValue,
    }: ResolverProps<K>): FilterOptions<FindContactRequestFiltersDto> {
      const options =
        filteringOptionsData?.data?.[key]?.map((item) => ({
          label: resolveLabel(item),
          value: resolveValue(item),
        })) ?? []

      return {
        label: fieldLabel,
        key: fieldKey,
        type: "async-multi-select",
        options,
        asyncRefetch: async (searchQuery?: string) => {
          const result = await findSupplierMappingFilteringOptions()
          const data = result.data?.[key] ?? []

          if (searchQuery) {
            const query = searchQuery.toLowerCase()
            return data
              .filter(
                (item) =>
                  resolveLabel(item).toLowerCase().includes(query) ?? false
              )
              .map((item) => ({
                label: resolveLabel(item),
                value: resolveValue(item),
              }))
          }

          return data.map((item) => ({
            label: resolveLabel(item),
            value: resolveValue(item),
          }))
        },
        isAsyncLoading: isFilteringOptionsLoading,
      }
    },
    [filteringOptionsData?.data, isFilteringOptionsLoading]
  )
}
